import React, { useEffect, useMemo, useState } from 'react';
import { Col, Empty, Pagination, Row, Typography, Card, Button, message } from 'antd';
import {
  useGetSegmentationClassesQuery,
  useGetViewsQuery,
  useGetPredictionFramesQuery,
} from '../../redux/slices/views';
import { AdvancedFiltersOptions, AdvancedFilterValue } from 'models/advancedFilters';
import { View } from 'models/views';
import Filters from 'components/Filters/Filters';
import Skeleton from 'components/Skeleton/Skeleton';
import { useSegmentationPredictionFilterState } from '../../hooks/useFilterState';
import { useSearchParams } from 'react-router-dom';
import {
  PredictionsWrapper,
  StatsBarWrapper,
  StatsContentWrapper,
  AcceptAllButtonWrapper,
  PaginationWrapper,
} from './styled';
import {
  Frame,
  PredictionFrame,
  SegmentationLabel,
  SegmentationPrediction,
  PredictionParams,
} from 'models/segmentation';
import SegmentationItem from '../SegmentationLabels/components/SegmentationItem';
import { Decision } from 'models/reviews';
import {
  useCreateReviewMutation,
  useGetSegmentationPredictionStatsQuery,
} from '../../redux/slices/reviews';
import RejectPredictionsModal from './components/RejectPredictionsModal';
import AcceptModal from '../SegmentationLabels/components/AcceptModal';
import ReviewModal from '../SegmentationLabels/components/ReviewModal';

const SegmentationPredictions: React.FC = () => {
  const { filterState, resetFilters, updateFilterTags } = useSegmentationPredictionFilterState();

  const hasAnyFilter = (fs: any) => {
    if (fs.view) return true;
    if (fs.features && fs.features.length > 0) return true;
    if (fs.advancedFilters) {
      return Object.values(fs.advancedFilters).some(
        v =>
          (Array.isArray(v) && v.length > 0) ||
          (typeof v === 'object' && v !== null && Object.keys(v).length > 0 && 'name' in v) ||
          (typeof v === 'string' && v.trim() !== '')
      );
    }
    return false;
  };

  const { data: views } = useGetViewsQuery('');
  const {
    data: segmentationClasses,
    isLoading: isLoadingSegmentationClasses,
    isError: isSegmentationClassesError,
  } = useGetSegmentationClassesQuery({});

  const [page, setPage] = useState(1);
  const [size, setSize] = useState(6);
  const [searchParams, setSearchParams] = useSearchParams();

  const [viewOptions, setViewOptions] = useState<AdvancedFilterValue[]>([]);
  const [featureOptions, setFeatureOptions] = useState<AdvancedFilterValue[]>([]);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [hasAutoAppliedFromQueryParams, setHasAutoAppliedFromQueryParams] = useState(false);
  const hasAnyQueryParam = useMemo(
    () =>
      [
        'view',
        'features',
        'datasources',
        'dicomICID',
        'study',
        'segmentationICID',
        'keypointICID',
        'technician',
        'source_name',
        'created_at_after',
        'created_at_before',
        'updated_at_after',
        'updated_at_before',
        'frame_id',
        'review_status',
      ].some(param => searchParams.get(param)),
    [searchParams]
  );
  const [shouldFetchFrames, setShouldFetchFrames] = useState(false);

  const [advancedFiltersOptions] = useState<AdvancedFiltersOptions>({
    datasources: [
      { uuid: 'ABBOTT', name: 'ABBOTT' },
      { uuid: 'ARKHEART', name: 'ARKHEART' },
      { uuid: 'CONWAY', name: 'CONWAY' },
      { uuid: 'DP', name: 'DP' },
      { uuid: 'FSV', name: 'FSV' },
    ],
    dicomICID: '',
    study: '',
    segmentationICID: '',
    technician: [],
    created_at_after: undefined,
    created_at_before: undefined,
    updated_at_after: undefined,
    updated_at_before: undefined,
  });

  const queryParams = useMemo(
    () => ({
      page,
      size,
      view: filterState.view,
      features: filterState.features,
      advancedFilters: {
        datasources: Array.isArray(filterState.advancedFilters.datasources)
          ? filterState.advancedFilters.datasources[0]
          : filterState.advancedFilters.datasources,
        dicomICID: filterState.advancedFilters.dicomICID,
        study: filterState.advancedFilters.study,
        segmentationICID: filterState.advancedFilters.segmentationICID,
        keypointICID: filterState.advancedFilters.keypointICID,
        frameId: (filterState.advancedFilters as any).frameId,
        reviewStatus: (filterState.advancedFilters as any).reviewStatus,
        technician: filterState.advancedFilters.technician,
        source_name: filterState.advancedFilters.source_name,
        created_at_after: filterState.advancedFilters.created_at_after,
        created_at_before: filterState.advancedFilters.created_at_before,
        updated_at_after: filterState.advancedFilters.updated_at_after,
        updated_at_before: filterState.advancedFilters.updated_at_before,
      },
    }),
    [page, size, filterState]
  );

  const {
    data: framesPage,
    isLoading: isLoadingGetFrames,
    isFetching,
    refetch,
  } = useGetPredictionFramesQuery(queryParams as PredictionParams, {
    skip: !shouldFetchFrames,
  });

  // read initial page from URL (?page=...), default to 1
  useEffect(() => {
    const qp = searchParams.get('page');
    const next = qp ? parseInt(qp, 10) : 1;
    if (Number.isFinite(next) && next > 0 && next !== page) {
      setPage(next);
    }
  }, [searchParams]);

  // If ?page is present, auto-enable fetching (even before Apply)
  useEffect(() => {
    const hasPage = !!searchParams.get('page');
    if (hasPage && !shouldFetchFrames) {
      setShouldFetchFrames(true);
    }
  }, [searchParams, shouldFetchFrames]);

  // keep skeleton up until fetch settles
  useEffect(() => {
    if (!isFetching && isPaginationLoading) {
      setIsPaginationLoading(false);
    }
  }, [isFetching, isPaginationLoading]);

  useEffect(() => {
    setIsPaginationLoading(true);
  }, [filterState]);

  const predictionsPage = {
    items: (framesPage?.results as unknown as PredictionFrame[]) || [],
    pages: Math.ceil(((framesPage?.count as number) || 0) / size),
    size: size,
    total: (framesPage?.count as number) || 0,
  };

  const [visibleSegmentations, setVisibleSegmentations] = useState<Map<string, Set<string>>>(
    new Map()
  );

  useEffect(() => {
    if (predictionsPage.items.length > 0) {
      const initialVisibility = new Map<string, Set<string>>();
      predictionsPage.items.forEach(frame => {
        const frameKey = frame.icid;
        const segIds = new Set((frame.segmentationPredictions || []).map(seg => seg.icid));
        initialVisibility.set(frameKey, segIds);
      });
      setVisibleSegmentations(initialVisibility);
    }
  }, [framesPage]);

  // Local decisions (keyed by ICID)
  const [createReview] = useCreateReviewMutation();
  const [localDecisions, setLocalDecisions] = useState<Record<string, Decision>>({});
  const getLocalDecision = (id: string) => localDecisions[id];
  const setDecisionsFor = (ids: string[], decision: Decision) => {
    setLocalDecisions(prev => {
      const next = { ...prev };
      ids.forEach(id => {
        next[id] = decision;
      });
      return next;
    });
  };
  const onLocalDecision = (id: string, decision: Decision) => setDecisionsFor([id], decision);
  const onLocalDecisionRemove = (id: string) =>
    setLocalDecisions(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  const [removedReviews, setRemovedReviews] = useState<Record<string, boolean>>({});
  const handleMarkRemoved = (ids: string[]) => {
    setRemovedReviews(prev => {
      const next = { ...prev };
      ids.forEach(id => {
        next[id] = true;
      });
      return next;
    });
    // clear local decisions for those ids
    setLocalDecisions(prev => {
      const next = { ...prev };
      ids.forEach(id => delete next[id]);
      return next;
    });
  };

  const [confirmAcceptAllOpen, setConfirmAcceptAllOpen] = useState(false);
  const [isAcceptAllLoading, setIsAcceptAllLoading] = useState(false);
  const [allReviewedOnPage, setAllReviewedOnPage] = useState(false);

  const doAcceptAllOnPage = async () => {
    setIsAcceptAllLoading(true);
    const predictionsToAccept = predictionsPage.items
      .flatMap(pf => adaptPredictionsToLabels(pf).labels)
      .filter(pred => {
        const local = getLocalDecision(pred.icid);
        const hasServerDecision = pred.isApproved || pred.isRejected;
        const hasAnyDecision = local !== undefined || hasServerDecision;
        return !hasAnyDecision;
      });

    if (predictionsToAccept.length === 0) {
      message.info('All predictions on this page have already been reviewed.');
      setIsAcceptAllLoading(false);
      setConfirmAcceptAllOpen(false);
      return;
    }

    try {
      await handleFrameAccept(predictionsToAccept);
      setConfirmAcceptAllOpen(false);
    } finally {
      setIsAcceptAllLoading(false);
    }
  };

  const handleFrameAccept = async (segmentations: SegmentationLabel[]) => {
    const reviewsToCreate = segmentations.map(seg => ({
      object_uuid: seg.uuid,
      object_icid: seg.icid,
      object_type: 'SEGMENTATION_PREDICTION',
      object_view: 'default',
      object_view_id: 0,
      decision: Decision.ACCEPTED,
      comment: '',
      annotation_link: '',
    }));
    try {
      await createReview(reviewsToCreate).unwrap();
      message.success('All predictions in this frame have been accepted.');
      setDecisionsFor(
        segmentations.map(s => s.icid),
        Decision.ACCEPTED
      );
    } catch (error) {
      message.error('Failed to accept predictions.');
    }
  };

  // Reject modal
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectFrameSegmentations, setRejectFrameSegmentations] = useState<SegmentationLabel[]>([]);
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [acceptFrameSegmentations, setAcceptFrameSegmentations] = useState<SegmentationLabel[]>([]);

  const openRejectModal = (_frameKey: string, segmentations: SegmentationLabel[]) => {
    setRejectFrameSegmentations(segmentations);
    setRejectModalOpen(true);
  };

  const handleRejectModalOk = async (rejectedIds: string[], comment: string) => {
    if (!rejectFrameSegmentations) return;
    try {
      // Only submit reviews for explicitly rejected predictions.
      const toReject = rejectFrameSegmentations.filter(seg => rejectedIds.includes(seg.icid));
      if (toReject.length === 0) {
        message.info('No predictions selected for rejection.');
        setRejectModalOpen(false);
        setRejectFrameSegmentations([]);
        return;
      }

      const reviewsToCreate = toReject.map(seg => ({
        object_uuid: seg.uuid,
        object_icid: seg.icid,
        object_type: 'SEGMENTATION_PREDICTION',
        object_view: 'default',
        object_view_id: 0,
        decision: Decision.REJECTED,
        comment,
        annotation_link: '',
      }));
      await createReview(reviewsToCreate).unwrap();
      message.success('Rejected predictions submitted successfully');

      const rejectedIcids: string[] = toReject.map(s => s.icid);
      if (rejectedIcids.length) setDecisionsFor(rejectedIcids, Decision.REJECTED);
    } catch (error) {
      message.error('Failed to submit review');
    }
    setRejectModalOpen(false);
    setRejectFrameSegmentations([]);
  };

  const handleRejectModalCancel = () => {
    setRejectModalOpen(false);
    setRejectFrameSegmentations([]);
  };

  const openAcceptModal = (segmentations: SegmentationLabel[]) => {
    // Open accept modal to choose which predictions to accept
    setAcceptFrameSegmentations(segmentations);
    setAcceptModalOpen(true);
  };

  const handleAcceptModalOk = async (acceptedIds: string[], comment: string) => {
    if (!acceptFrameSegmentations) return;
    try {
      // Only submit reviews for explicitly accepted predictions.
      const toAccept = acceptFrameSegmentations.filter(seg => acceptedIds.includes(seg.icid));
      if (toAccept.length === 0) {
        message.info('No predictions selected for acceptance.');
        setAcceptModalOpen(false);
        setAcceptFrameSegmentations([]);
        return;
      }

      const reviewsToCreate = toAccept.map(seg => ({
        object_uuid: seg.uuid,
        object_icid: seg.icid,
        object_type: 'SEGMENTATION_PREDICTION',
        object_view: 'default',
        object_view_id: 0,
        decision: Decision.ACCEPTED,
        comment,
        annotation_link: '',
      }));
      try {
        await createReview(reviewsToCreate).unwrap();
        message.success('Selected predictions accepted successfully');
        const acceptedIcids: string[] = toAccept.map(s => s.icid);
        if (acceptedIcids.length) setDecisionsFor(acceptedIcids, Decision.ACCEPTED);
      } catch (error) {
        message.error('Failed to accept selected predictions.');
      }
    } catch (error) {
      message.error('Failed to submit review');
    }
    setAcceptModalOpen(false);
    setAcceptFrameSegmentations([]);
  };

  useEffect(() => {
    let viewList: View[] = [];
    if (
      views &&
      typeof views === 'object' &&
      'results' in views &&
      Array.isArray((views as any).results)
    ) {
      viewList = (views as { results: View[] }).results;
    } else if (Array.isArray(views)) {
      viewList = views as View[];
    }

    if (viewList.length > 0) {
      const sortedViews = [...viewList].sort((a, b) => a.view.localeCompare(b.view));
      const formattedView = sortedViews.map((view: View) => ({ uuid: view.uuid, name: view.view }));
      setViewOptions(formattedView);
    } else {
      setViewOptions([]);
    }
  }, [views]);

  useEffect(() => {
    if (!segmentationClasses) {
      setFeatureOptions([]);
      return;
    }
    let items: any[] = [];
    if (Array.isArray(segmentationClasses)) {
      items = segmentationClasses as any[];
    } else if (
      typeof segmentationClasses === 'object' &&
      'results' in (segmentationClasses as any) &&
      Array.isArray((segmentationClasses as any).results)
    ) {
      items = (segmentationClasses as any).results;
    }
    const sorted = [...items].sort((a, b) => String(a.value).localeCompare(String(b.value)));
    const features: AdvancedFilterValue[] = sorted.map((item: any) => ({
      uuid: item.uuid,
      name: item.value,
      acronym: item.acronym,
    }));
    setFeatureOptions(features);
  }, [segmentationClasses]);

  useEffect(() => {
    if (framesPage && isPaginationLoading) setIsPaginationLoading(false);
  }, [framesPage, isPaginationLoading]);

  const isFilterStateSyncedWithQuery = (fs: any) => {
    const viewParam = searchParams.get('view');
    if (viewParam) {
      const matchesView = fs.view && (fs.view.name === viewParam || fs.view.uuid === viewParam);
      if (!matchesView) return false;
    }

    const featuresParam = searchParams.get('features');
    if (featuresParam) {
      const featureValues = featuresParam
        .split(',')
        .map(f => decodeURIComponent(f).replace(/\+/g, ' '));
      const matchesFeatures =
        fs.features.length === featureValues.length &&
        fs.features.every(
          (f: AdvancedFilterValue) =>
            featureValues.includes(f.name) || featureValues.includes(f.uuid)
        );
      if (!matchesFeatures) return false;
    } else if (fs.features && fs.features.length > 0) {
      return false;
    }

    const datasourceParam = searchParams.get('datasources');
    if (datasourceParam) {
      const matchesDatasource =
        fs.advancedFilters.datasources &&
        (fs.advancedFilters.datasources.name === datasourceParam ||
          fs.advancedFilters.datasources.uuid === datasourceParam);
      if (!matchesDatasource) return false;
    }

    const dicomICIDParam = searchParams.get('dicomICID');
    if (dicomICIDParam) {
      const matchesDicomICID = fs.advancedFilters.dicomICID === dicomICIDParam;
      if (!matchesDicomICID) return false;
    }

    const studyParam = searchParams.get('study');
    if (studyParam) {
      const matchesStudy = fs.advancedFilters.study === studyParam;
      if (!matchesStudy) return false;
    }

    const segmentationICIDParam = searchParams.get('segmentationICID');
    if (segmentationICIDParam) {
      const matchesSegmentationICID = fs.advancedFilters.segmentationICID === segmentationICIDParam;
      if (!matchesSegmentationICID) return false;
    }

    const keypointICIDParam = searchParams.get('keypointICID');
    if (keypointICIDParam) {
      const matchesKeypointICID = fs.advancedFilters.keypointICID === keypointICIDParam;
      if (!matchesKeypointICID) return false;
    }

    const technicianParam = searchParams.get('technician');
    if (technicianParam) {
      const matchesTechnician =
        Array.isArray(fs.advancedFilters.technician) &&
        technicianParam
          .split(',')
          .every((tech: string) =>
            fs.advancedFilters.technician.some(
              (t: AdvancedFilterValue) => t.name === tech || t.uuid === tech
            )
          );
      if (!matchesTechnician) return false;
    }

    const sourceNameParam = searchParams.get('source_name');
    if (sourceNameParam) {
      const matchesSourceName = fs.advancedFilters.source_name === sourceNameParam;
      if (!matchesSourceName) return false;
    }

    const createdAtAfterParam = searchParams.get('created_at_after');
    if (createdAtAfterParam) {
      const matchesCreatedAtAfter = fs.advancedFilters.created_at_after === createdAtAfterParam;
      if (!matchesCreatedAtAfter) return false;
    }

    const createdAtBeforeParam = searchParams.get('created_at_before');
    if (createdAtBeforeParam) {
      const matchesCreatedAtBefore = fs.advancedFilters.created_at_before === createdAtBeforeParam;
      if (!matchesCreatedAtBefore) return false;
    }

    const updatedAtAfterParam = searchParams.get('updated_at_after');
    if (updatedAtAfterParam) {
      const matchesUpdatedAtAfter = fs.advancedFilters.updated_at_after === updatedAtAfterParam;
      if (!matchesUpdatedAtAfter) return false;
    }

    const updatedAtBeforeParam = searchParams.get('updated_at_before');
    if (updatedAtBeforeParam) {
      const matchesUpdatedAtBefore = fs.advancedFilters.updated_at_before === updatedAtBeforeParam;
      if (!matchesUpdatedAtBefore) return false;
    }
    // --- Frame ID param ---
    const frameIdParam = searchParams.get('frame_id');
    if (frameIdParam) {
      const matchesFrameId =
        (fs.advancedFilters as any).frameId === decodeURIComponent(frameIdParam);
      if (!matchesFrameId) return false;
    }
    // --- Review status param (comma-separated) ---
    const reviewStatusParam = searchParams.get('review_status');
    if (reviewStatusParam) {
      const requested = decodeURIComponent(reviewStatusParam)
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      const current = Array.isArray((fs.advancedFilters as any).reviewStatus)
        ? (fs.advancedFilters as any).reviewStatus.map((s: string) => String(s))
        : [];
      const allIncluded = requested.every((r: string) => current.includes(r));
      if (!allIncluded) return false;
    }

    return true;
  };

  useEffect(() => {
    if (!hasAutoAppliedFromQueryParams && hasAnyQueryParam) {
      const hydrated = hasAnyFilter(filterState);
      const synced = isFilterStateSyncedWithQuery(filterState);
      if (hydrated && synced) {
        setHasAutoAppliedFromQueryParams(true);
        setShouldFetchFrames(true);
      }
    }
  }, [hasAutoAppliedFromQueryParams, hasAnyQueryParam, filterState]);

  const onHandleClear = () => {
    resetFilters();
    setPage(1);
    setIsPaginationLoading(false);
    setShouldFetchFrames(false);
    // ensure URL contains page=1 when clearing filters
    writePageToURL(1);
  };

  const onHandleApply = () => {
    setIsPaginationLoading(false);
    setPage(1);
    updateFilterTags(); // Update tags on apply
    setShouldFetchFrames(true);

    const params: Record<string, string> = {};

    if (filterState.view?.name) {
      params['view'] = filterState.view.name;
    }
    if (filterState.features.length) {
      params['features'] = filterState.features.map((f: AdvancedFilterValue) => f.name).join(',');
    }

    if (filterState.advancedFilters.datasources) {
      const datasource = filterState.advancedFilters.datasources;
      let datasourceName: string | undefined;
      if (Array.isArray(datasource)) {
        if (datasource.length > 0) {
          datasourceName = datasource[0].name;
        }
      } else if (datasource && typeof datasource === 'object' && 'name' in datasource) {
        datasourceName = (datasource as AdvancedFilterValue).name;
      }
      if (datasourceName) {
        params['datasources'] = datasourceName;
      }
    }

    if (filterState.advancedFilters.dicomICID) {
      params['dicomICID'] = filterState.advancedFilters.dicomICID;
    }
    if (filterState.advancedFilters.study) {
      params['study'] = filterState.advancedFilters.study;
    }
    if (filterState.advancedFilters.segmentationICID) {
      params['segmentationICID'] = filterState.advancedFilters.segmentationICID;
    }
    if (filterState.advancedFilters.keypointICID) {
      params['keypointICID'] = filterState.advancedFilters.keypointICID;
    }
    if (
      filterState.advancedFilters.technician &&
      Array.isArray(filterState.advancedFilters.technician) &&
      filterState.advancedFilters.technician.length > 0
    ) {
      params['technician'] = filterState.advancedFilters.technician.map(t => t.name).join(',');
    }
    if (filterState.advancedFilters.created_at_after) {
      params['created_at_after'] = filterState.advancedFilters.created_at_after;
    }
    if (filterState.advancedFilters.created_at_before) {
      params['created_at_before'] = filterState.advancedFilters.created_at_before;
    }
    if (filterState.advancedFilters.updated_at_after) {
      params['updated_at_after'] = filterState.advancedFilters.updated_at_after;
    }
    if (filterState.advancedFilters.updated_at_before) {
      params['updated_at_before'] = filterState.advancedFilters.updated_at_before;
    }

    if (
      filterState.advancedFilters.source_name &&
      typeof filterState.advancedFilters.source_name === 'string' &&
      filterState.advancedFilters.source_name.trim()
    ) {
      params['source_name'] = filterState.advancedFilters.source_name;
    }
    // New: frame id and review status
    if ((filterState.advancedFilters as any).frameId) {
      params['frame_id'] = (filterState.advancedFilters as any).frameId;
    }
    if (
      (filterState.advancedFilters as any).reviewStatus &&
      Array.isArray((filterState.advancedFilters as any).reviewStatus) &&
      (filterState.advancedFilters as any).reviewStatus.length > 0
    ) {
      params['review_status'] = (filterState.advancedFilters as any).reviewStatus.join(',');
    }

    setSearchParams(params);
    // ensure page=1 is present in the URL after applying filters
    writePageToURL(1);
  };

  const adaptPredictionsToLabels = (
    frame: PredictionFrame
  ): { frame: Frame; labels: SegmentationLabel[] } => {
    const labels: SegmentationLabel[] = (frame.segmentationPredictions || []).map(
      (pred: SegmentationPrediction) => ({
        icid: pred.icid,
        uuid: pred.uuid,
        createdAt: pred.createdAt || '',
        datasourceName: (pred as any).datasourceName || (pred as any).sourceName || '',
        dicomIcid: (pred as any).dicomIcid || (pred as any).dicom || '',
        dicomUuid: (pred as any).dicomUuid || (pred as any).dicom || '',
        frameIcid: (pred as any).frameIcid || (pred as any).frame || '',
        frameIndex: pred.frameIndex,
        frameUuid: (pred as any).frameUuid || (pred as any).frame || '',
        isApproved: (pred as any).isApproved || false,
        isFullyVisible: (pred as any).isFullyVisible ?? true,
        isRejected: (pred as any).isRejected || false,
        isReviewed: (pred as any).isReviewed || false,
        paths: pred.paths || [],
        segmentationClassValue: pred.segmentationClassValue,
        // accept both camelCase and snake_case from backend
        fileUrl: (pred as any).fileUrl || (pred as any).file_url || undefined,
        segmentationColor: (pred as any).segmentationColor,
      })
    );

    return {
      frame: {
        dicomUuid: (frame as any).dicomUuid || '',
        // backend may return fileUrl in several shapes (camelCase or snake_case),
        // or nested under a `frame` key in some responses — accept common fallbacks.
        fileUrl:
          (frame as any).fileUrl ||
          (frame as any).file_url ||
          ((frame as any).frame &&
            ((frame as any).frame.fileUrl || (frame as any).frame.file_url)) ||
          '',
        icid: frame.icid,
        index: frame.index,
        segmentationLabels: labels,
      },
      labels,
    };
  };

  useEffect(() => {
    if (framesPage) {
      const allReviewed = predictionsPage.items.every(frameGroup =>
        frameGroup.segmentationPredictions?.every(pred => {
          const wasRemoved = removedReviews ? !!removedReviews[(pred as any).icid] : false;
          return !wasRemoved && (pred as any).isReviewed;
        })
      );
      setAllReviewedOnPage(allReviewed);
    }
  }, [framesPage]);

  const { data: segmentationPredictionStats, refetch: refetchSegmentationPredictionStats } =
    useGetSegmentationPredictionStatsQuery();

  // Refetch GLOBAL stats when filters are NOT applied (mirrors other page behavior)
  useEffect(() => {
    if (!shouldFetchFrames) {
      refetchSegmentationPredictionStats();
    }
  }, [shouldFetchFrames, refetchSegmentationPredictionStats]);

  const allAcceptedOrRejected = useMemo(() => {
    return allReviewedOnPage || isAcceptAllLoading;
  }, [allReviewedOnPage, isAcceptAllLoading]);

  const isAcceptAllDisabled = useMemo(() => {
    return !shouldFetchFrames || !predictionsPage.items.length;
  }, [shouldFetchFrames, predictionsPage.items.length]);

  // ------- NEW: choose which stats to render (frames vs global) and normalize keys -------
  const selectedStats = useMemo(() => {
    if (shouldFetchFrames && framesPage?.stats) {
      const s: any = framesPage.stats;
      return {
        total: s.total_found ?? s.totalFound ?? 0,
        accepted: s.accepted ?? 0,
        rejected: s.rejected ?? 0,
        toBeReviewed: s.to_be_reviewed ?? s.toBeReviewed ?? 0,
      };
    }
    if (segmentationPredictionStats) {
      const s: any = segmentationPredictionStats;
      return {
        total: s.totalFound ?? s.total_found ?? 0,
        accepted: s.accepted ?? 0,
        rejected: s.rejected ?? 0,
        toBeReviewed: s.toBeReviewed ?? s.to_be_reviewed ?? 0,
      };
    }
    return null;
  }, [shouldFetchFrames, framesPage, segmentationPredictionStats]);

  const writePageToURL = (next: number, nextSize?: number) => {
    const params = new URLSearchParams(window.location.search);
    params.set('page', String(next));
    const sizeToWrite = typeof nextSize === 'number' ? nextSize : size;
    params.set('items_per_page', String(sizeToWrite));
    setSearchParams(params, { replace: true });
  };

  // Sync initial size from URL (?items_per_page=...); default to current `size` if invalid/missing
  useEffect(() => {
    const qp = searchParams.get('items_per_page');
    const next = qp ? parseInt(qp, 10) : undefined;
    if (next && Number.isFinite(next) && next > 0 && next !== size) {
      setSize(next);
    }
  }, [searchParams]);

  const onPaginationChange = (_page: number, pageSize: number) => {
    setIsPaginationLoading(true);
    window.scrollTo({ top: 0, behavior: 'auto' });
    refetch();
    setPage(_page);
    setSize(pageSize);
    writePageToURL(_page, pageSize);
  };

  const onShowSizeChange = (_current: number, newSize: number) => {
    setIsPaginationLoading(true);
    window.scrollTo({ top: 0, behavior: 'auto' });
    refetch();
    setSize(newSize);
    setPage(1);
    writePageToURL(1, newSize);
  };

  return (
    <PredictionsWrapper>
      <Filters
        primaryOptions={viewOptions}
        secondaryOptions={featureOptions}
        advancedFiltersOptions={advancedFiltersOptions}
        handlePrimaryOptChange={() => undefined}
        onHandleClear={onHandleClear}
        onHandleApply={onHandleApply}
        isLoadingSegmentationClasses={isLoadingSegmentationClasses}
        isSegmentationClassesError={isSegmentationClassesError}
        clearSegmentations={onHandleClear}
        isSegmentationPredictionMode={true}
      />

      {/* Statistics Bar (frames stats after Apply; global stats otherwise) */}
      {selectedStats && (
        <StatsBarWrapper>
          <StatsContentWrapper>
            <Typography.Text style={{ fontSize: '16px', fontWeight: 'bold' }}>
              {selectedStats.total} Total
            </Typography.Text>
            <Typography.Text type="success" style={{ fontSize: '16px', fontWeight: 'bold' }}>
              {selectedStats.accepted} Accepted
            </Typography.Text>
            <Typography.Text type="danger" style={{ fontSize: '16px', fontWeight: 'bold' }}>
              {selectedStats.rejected} Rejected
            </Typography.Text>
            <Typography.Text type="warning" style={{ fontSize: '16px', fontWeight: 'bold' }}>
              {selectedStats.toBeReviewed} To-be-reviewed
            </Typography.Text>
          </StatsContentWrapper>

          {shouldFetchFrames && predictionsPage.items.length > 0 && (
            <AcceptAllButtonWrapper>
              <Button
                type="primary"
                onClick={() => setConfirmAcceptAllOpen(true)}
                disabled={allAcceptedOrRejected || isAcceptAllLoading || isAcceptAllDisabled}
                loading={isAcceptAllLoading}
                style={{ height: '40px', fontWeight: 'bold', fontSize: '16px' }}
              >
                Accept All On Page
              </Button>
            </AcceptAllButtonWrapper>
          )}
        </StatsBarWrapper>
      )}

      {!shouldFetchFrames ? (
        <Empty
          description={
            <Typography.Text type="secondary">
              Apply filters to view segmentation predictions.
            </Typography.Text>
          }
        />
      ) : isLoadingGetFrames || isFetching || isPaginationLoading ? (
        <Skeleton />
      ) : predictionsPage.items.length > 0 ? (
        <Row gutter={[24, 24]}>
          {predictionsPage.items.map((pf: PredictionFrame) => {
            const { frame, labels } = adaptPredictionsToLabels(pf);
            const frameKey = frame.icid;
            const frameVisibleSegmentations = visibleSegmentations.get(frameKey) || new Set();
            const allInFrameAccepted = labels.every(pred => {
              const local = getLocalDecision(pred.icid);
              const wasRemoved = removedReviews ? !!removedReviews[pred.icid] : false;
              const serverAccepted = !wasRemoved && !!pred.isApproved;
              return local === Decision.ACCEPTED || serverAccepted;
            });

            return (
              <Col key={frameKey} xs={24} sm={24} md={12} lg={8} xl={8}>
                <Card
                  title={
                    <div>
                      <strong>
                        Frame #{frame.index} (ICID: {frame.icid})
                      </strong>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        {labels.length} prediction{labels.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  }
                  style={{ minHeight: 400 }}
                  bordered={false}
                >
                  {labels.length === 0 ? (
                    <Typography.Text type="secondary">
                      No predictions for this frame.
                    </Typography.Text>
                  ) : (
                    <SegmentationItem
                      key={labels[0].icid}
                      segmentation={labels[0]}
                      frameFile={frame.fileUrl}
                      review={undefined}
                      acceptMe={false}
                      allSegmentations={labels}
                      onSegmentationToggle={(segmentationId, visible) =>
                        setVisibleSegmentations(prev => {
                          const next = new Map(prev);
                          const set = new Set(next.get(frameKey) || []);
                          if (visible) set.add(segmentationId);
                          else set.delete(segmentationId);
                          next.set(frameKey, set);
                          return next;
                        })
                      }
                      visibleSegmentations={frameVisibleSegmentations}
                      onFrameAccept={() => openAcceptModal(labels)}
                      onFrameReject={() => openRejectModal(frameKey, labels)}
                      frameReviewed={allInFrameAccepted}
                      onReviewChange={() => undefined}
                      onLocalDecision={onLocalDecision}
                      onLocalDecisionRemove={onLocalDecisionRemove}
                      getLocalDecision={getLocalDecision}
                      removedReviews={removedReviews}
                      onMarkRemoved={handleMarkRemoved}
                      isRejected={labels.some(s => s.isRejected)}
                      objectType={'SEGMENTATION_PREDICTION'}
                    />
                  )}
                </Card>
              </Col>
            );
          })}
        </Row>
      ) : (
        <Empty
          description={
            <Typography.Text type="secondary">
              No predictions found for the selected filters.
            </Typography.Text>
          }
        />
      )}

      {shouldFetchFrames &&
        predictionsPage.items.length > 0 &&
        !isLoadingGetFrames &&
        !isFetching &&
        !isPaginationLoading && (
          <PaginationWrapper>
            <Pagination
              total={predictionsPage.total}
              defaultPageSize={size}
              current={page}
              onShowSizeChange={onShowSizeChange}
              onChange={onPaginationChange}
              showTotal={total => `Total ${total} items`}
            />
          </PaginationWrapper>
        )}

      <RejectPredictionsModal
        isModalOpen={rejectModalOpen}
        handleCancel={handleRejectModalCancel}
        handleOk={handleRejectModalOk}
        segmentations={rejectFrameSegmentations}
        initialRejectedIds={rejectFrameSegmentations
          .filter(pred => {
            const local = getLocalDecision(pred.icid);
            return local === Decision.REJECTED || pred.isRejected;
          })
          .map(pred => pred.icid)}
      />
      <AcceptModal
        isModalOpen={acceptModalOpen}
        handleCancel={() => {
          setAcceptModalOpen(false);
          setAcceptFrameSegmentations([]);
        }}
        handleOk={handleAcceptModalOk}
        segmentations={acceptFrameSegmentations}
        initialAcceptedIds={acceptFrameSegmentations
          .filter(seg => {
            const d = getLocalDecision(seg.icid);
            return d === Decision.ACCEPTED || seg.isApproved;
          })
          .map(seg => seg.icid)}
      />
      <ReviewModal
        isModalOpen={confirmAcceptAllOpen}
        handleOk={doAcceptAllOnPage}
        handleCancel={() => setConfirmAcceptAllOpen(false)}
        loading={isAcceptAllLoading}
        subjectLabel="predictions"
      />
    </PredictionsWrapper>
  );
};

export default SegmentationPredictions;
